import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Check, CircleDot, Clock3, FileText, RefreshCw, ShieldCheck, Undo2 } from 'lucide-react';
import {
  activateAmendmentNow, apiErrorMessage, approveAmendment, decideContractTerm, getAmendmentReview, getFrozenAmendmentReview,
  resolveAmendmentFacility, withdrawAmendment,
} from '../api/abfApi';
import type { AmendmentReview, FrozenAmendmentReview } from '../types/amendmentReview';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { ViewportToast, type ActionFeedbackState } from '../components/ActionFeedback';
import { facilityWorkspacePath } from '../routes';
import { Button, INK, Input } from '../onboarding/review/reviewStyles';
import {
  DecisionBar, Header, Journey, Layout, Note, Readable, SmallButton, Surface, SummaryGrid, TaskButton, TaskRail, Work,
} from '../onboarding/review/contractReviewStyles';
import { GroupPane, Table, type TermDecisionControls, type TermDecisionInput } from './AmendmentGroupPane';
import { formatDate, formatDateTime, signed } from './amendmentPresentation';

const Ack = styled.label`
  display: flex; gap: 10px; align-items: flex-start; margin-top: 8px; color: ${INK.soft}; font-size: 13px; line-height: 1.5;
  input { margin-top: 3px; accent-color: ${INK.accent}; }
`;

const Panel = styled.div`
  margin-top: 10px; padding: 12px 14px; border: 1px solid ${INK.lineStrong}; border-radius: 9px; background: ${INK.well};
  h3 { margin: 0 0 6px; font-size: 14px; }
  p { color: ${INK.muted}; font-size: 13px; }
  textarea { width: 100%; box-sizing: border-box; min-height: 64px; margin-top: 6px; padding: 8px 10px; border: 1px solid ${INK.lineStrong}; border-radius: 8px; background: ${INK.panel}; color: ${INK.body}; font: inherit; font-size: 13px; }
  .row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
`;

const Candidate = styled.label<{ $active: boolean }>`
  display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center; padding: 10px 12px; margin-top: 6px;
  border: 1px solid ${p => p.$active ? INK.accent : INK.line}; border-radius: 8px; background: ${p => p.$active ? '#19334a' : INK.panel}; cursor: pointer;
  strong { display: block; } small { color: ${INK.muted}; }
`;

const Gates = styled.ul`
  list-style: none; margin: 0; padding: 0; display: grid; gap: 4px;
  li { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; color: ${INK.soft}; }
  li[data-passed='true'] { color: ${INK.muted}; }
`;

const tone = (state: string): StatusTone => state === 'ACTIVE' ? 'done'
  : state === 'SCHEDULED' ? 'progress' : ['WITHDRAWN', 'REJECTED'].includes(state) ? 'critical'
    : state === 'SUPERSEDED' ? 'neutral' : 'progress';

const QUEUE = '/portfolio-management?tab=credit-loans&sub=abf';

/** Slice 6 — the amendment review: one page for both lanes, grouped by consequence. */
const AmendmentReviewPage: React.FC = () => {
  const { facilityId: rawFacility, versionId: rawVersion } = useParams();
  const versionId = Number(rawVersion);
  const [review, setReview] = useState<AmendmentReview | null>(null);
  const [frozen, setFrozen] = useState<FrozenAmendmentReview | null>(null);
  const [showFrozen, setShowFrozen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [acks, setAcks] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [candidateId, setCandidateId] = useState<number | null>(null);
  const [bindReason, setBindReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async () => {
    if (!Number.isInteger(versionId) || versionId <= 0) { setError('This amendment link is invalid.'); setLoading(false); return; }
    setLoading(true);
    try {
      const next = await getAmendmentReview(versionId);
      if (!alive.current) return;
      setReview(next); setError(null);
    } catch (failure) {
      if (alive.current) setError(apiErrorMessage(failure, 'The amendment review could not be loaded.'));
    } finally { if (alive.current) setLoading(false); }
  }, [versionId]);
  useEffect(() => { void load(); }, [load]);

  const groups = useMemo(() => review?.groups || [], [review]);
  // Terms still to confirm come first: nothing else on the page can be approved until they are decided.
  const current = useMemo(() => groups.find(group => group.key === selectedGroup)
    || groups.find(group => (group.pendingDecisions || []).length > 0)
    || groups.find(group => group.changed > 0) || groups[0] || null, [groups, selectedGroup]);
  const pendingTerms = groups.find(group => group.key === 'TERMS')?.pendingDecisions?.length || 0;

  const act = async (key: string, operation: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      await operation();
      setFeedback({ tone: 'success', message: success });
      await load();
    } catch (failure) {
      setFeedback({ tone: 'error', message: apiErrorMessage(failure, 'The action was refused.') });
    } finally { setBusy(null); }
  };

  // Walkthrough 2026-09-22: extracted terms are confirmed here, through the same governed term
  // decision the onboarding review uses; a refusal is shown on the row and in the toast.
  const decideTerm = async (termKey: string, input: TermDecisionInput) => {
    setBusy(`term:${termKey}`);
    try {
      await decideContractTerm(versionId, termKey, input);
      setFeedback({ tone: 'success', message: input.state === 'NOT_STATED' ? 'Recorded as not stated in the document.' : 'Term confirmed.' });
      await load();
    } catch (failure) {
      const message = apiErrorMessage(failure, 'The term decision was refused.');
      setFeedback({ tone: 'error', message });
      throw new Error(message);
    } finally { setBusy(null); }
  };
  const termControls: TermDecisionControls = {
    canDecide: Boolean(review?.actorMayPrepare) && ['EXTRACTED', 'REVIEWED', 'VERIFIED'].includes(review?.state || ''),
    busyKey: busy && busy.startsWith('term:') ? busy.slice(5) : null,
    decide: decideTerm,
  };

  const openFrozen = async () => {
    try {
      setFrozen(await getFrozenAmendmentReview(versionId));
      setShowFrozen(true);
    } catch (failure) {
      setFeedback({ tone: 'error', message: apiErrorMessage(failure, 'The signed statement could not be loaded.') });
    }
  };

  if (!review) return <Surface>
    <Note role={error ? 'alert' : 'status'}>
      <p>{error || 'Loading the amendment review…'}</p>
      {error && <SmallButton type="button" onClick={() => void load()} disabled={loading}>Retry</SmallButton>}
      <p><Link to={QUEUE} style={{ color: INK.accentSoft }}>Back to the agreement queue</Link></p>
    </Note>
  </Surface>;

  const facilityId = review.facilityId ?? Number(rawFacility);
  const decision = review.decision;
  const required = review.acknowledgementsRequired || [];
  const allAcked = required.every(item => acks.has(item.key));
  const contractHref = Number.isInteger(facilityId) && facilityId > 0 ? facilityWorkspacePath(facilityId, 'contract') : QUEUE;
  const metrics = review.metrics;
  const shown = showFrozen && frozen ? frozen.review : review;
  const shownGroups = shown.groups || [];
  const shownCurrent = shownGroups.find(group => group.key === (current?.key || '')) || shownGroups[0] || null;

  const primaryButton = () => {
    switch (decision.action) {
      case 'APPROVE_AND_ACTIVATE':
      case 'APPROVE_AND_SCHEDULE':
        return <Button $variant="primary" type="button" disabled={busy !== null || !decision.enabled || !allAcked}
          onClick={() => void act('approve', () => approveAmendment(versionId, { notes: notes.trim() || undefined, acknowledgements: Array.from(acks) }),
            decision.action === 'APPROVE_AND_SCHEDULE' ? `Approved. Version ${review.versionNo} takes effect on ${formatDate(review.effectiveFrom)}.` : `Version ${review.versionNo} is active.`)}>
          <ShieldCheck size={14} /> {busy === 'approve' ? 'Approving…' : decision.label}
        </Button>;
      case 'ACTIVATE_NOW':
        return <Button $variant="primary" type="button" disabled={busy !== null || !decision.enabled}
          onClick={() => void act('activate', () => activateAmendmentNow(versionId), `Version ${review.versionNo} is active.`)}>
          <ShieldCheck size={14} /> {busy === 'activate' ? 'Activating…' : 'Activate now'}
        </Button>;
      case 'REVIEW_TERMS':
        return <Button $variant="primary" type="button" disabled={!decision.enabled}
          onClick={() => { setShowFrozen(false); setSelectedGroup('TERMS'); window.setTimeout(() => document.getElementById('amendment-terms-to-confirm')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0); }}>
          {pendingTerms > 0 ? `${pendingTerms} term${pendingTerms === 1 ? '' : 's'} to confirm` : 'Confirm the extracted terms'}
        </Button>;
      case 'RESOLVE_FACILITY':
        return <Button $variant="primary" type="button" disabled={busy !== null || !candidateId || !bindReason.trim()}
          onClick={() => void act('bind', () => resolveAmendmentFacility(versionId, { facilityId: Number(candidateId), reason: bindReason.trim() }), 'Facility bound; the review is computed against its active version.')}>
          {busy === 'bind' ? 'Binding…' : 'Bind to the chosen facility'}
        </Button>;
      case 'ACTIVATED':
        return <Link to={contractHref}><Button type="button">Open the contract</Button></Link>;
      default:
        return <Button type="button" disabled>{decision.label}</Button>;
    }
  };

  return <Surface>
    <Header>
      <Link className="back" to={contractHref}><ArrowLeft size={14} /> Contract</Link>
      <div className="heading">
        <div>
          <div className="eyebrow">Amendment · version {review.predecessorVersionNo != null ? `${review.predecessorVersionNo} → ` : ''}{review.versionNo}</div>
          <h1>{review.facilityName || (review.facilityUnresolved?.facilityName) || 'Facility amendment'}</h1>
          <div className="meta">
            {review.facilityCode && <span>{review.facilityCode}</span>}
            <span><Clock3 size={14} /> Effective {formatDate(review.effectiveFrom)}{review.futureDated ? ' (ahead of the business date)' : review.backdated ? ' (before the business date)' : ''}</span>
            <span><FileText size={14} /> {review.lane === 'DOCUMENT' ? 'From the amendment document' : 'Entered by hand'}{review.authority && typeof review.authority === 'object' && 'authorityReference' in review.authority ? ` · ${String(review.authority.authorityReference)}` : ''}</span>
            <span>Business date {formatDate(review.businessDate)}</span>
          </div>
        </div>
        <div className="actions">
          <StatusChip tone={tone(review.state)} label={review.stateLabel} size="md" />
          <SmallButton type="button" onClick={() => void load()} disabled={loading || busy !== null}><RefreshCw size={14} />{loading ? 'Refreshing…' : 'Refresh'}</SmallButton>
        </div>
      </div>
    </Header>

    {error && <Note role="alert" style={{ marginBottom: 16 }}>{error} Displayed information may be out of date.</Note>}

    <Journey aria-label="Amendment progress" style={{ gridTemplateColumns: `repeat(${review.journey.length}, 1fr)` }}>
      {review.journey.map((step, index) => <li key={step.key} data-current={step.state === 'current'} aria-current={step.state === 'current' ? 'step' : undefined}>
        <span className={`step-icon ${step.state === 'done' ? 'done' : ''}`}>{step.state === 'done' ? <Check size={15} /> : <CircleDot size={15} />}</span>
        <div><strong>{index + 1}. {step.label}</strong><small>{step.detail}</small></div>
      </li>)}
    </Journey>

    {metrics && <SummaryGrid aria-label="Amendment summary">
      <div><span>Versions</span><strong>{metrics.versions}</strong></div>
      <div><span>Terms · schedules changed</span><strong>{metrics.termsChanged} · {metrics.schedulesChanged}</strong></div>
      <div><span>Borrowing base</span><strong>{metrics.borrowingBaseDelta == null ? 'Unaffected' : signed(metrics.borrowingBaseDelta)}</strong></div>
      <div><span>Book of record</span><strong style={{ fontSize: 13 }}>{metrics.bookOfRecord || '—'}</strong></div>
    </SummaryGrid>}

    {review.facilityUnresolved && <Note role="status" style={{ marginBottom: 16 }}>
      <strong>This amendment names a facility AxiomFlow could not match</strong>
      <p>Code {review.facilityUnresolved.facilityCode} · name {review.facilityUnresolved.facilityName}. Pick the facility it belongs to and say why; the review is then computed against that facility's active version.</p>
      <div>{(review.facilityCandidates || []).map(candidate => <Candidate key={candidate.id} $active={candidateId === candidate.id}>
        <input type="radio" name="facility-candidate" checked={candidateId === candidate.id} onChange={() => setCandidateId(candidate.id)} />
        <div><strong>{candidate.facility_name}</strong><small>{candidate.facility_code} · {candidate.status.toLowerCase()} · {candidate.active_version_no != null ? `active version ${candidate.active_version_no}` : 'no active version'}</small></div>
        <small>{Math.round(Number(candidate.score) * 100)}% match</small>
      </Candidate>)}</div>
      <Input style={{ marginTop: 10 }} placeholder="Why this facility is the one the amendment names" value={bindReason} onChange={event => setBindReason(event.target.value)} />
    </Note>}

    {(review.closedPeriodsReached || []).length > 0 && <Note role="status" style={{ marginBottom: 16 }}>
      <strong>Reaches {review.closedPeriodsReached!.length} closed period{review.closedPeriodsReached!.length === 1 ? '' : 's'}</strong>
      {review.closedPeriodsReached!.map(period => <p key={period.id}>{period.period_label} ({period.status.replace(/_/g, ' ').toLowerCase()}): {period.note}</p>)}
    </Note>}

    {shownGroups.length > 0 && <Layout>
      <TaskRail aria-label="Consequence groups">
        <div className="rail-heading"><h2>What changes</h2><p>By consequence. Select a group to read its before and after.</p></div>
        <div className="tasks">
          {shownGroups.map(group => <TaskButton key={group.key} type="button" $active={shownCurrent?.key === group.key} aria-pressed={shownCurrent?.key === group.key} onClick={() => setSelectedGroup(group.key)}>
            <strong>{group.label}</strong>
            <span className={group.changed > 0 ? 'reason' : 'owner'}>{group.changed > 0 ? `${group.changed} change${group.changed === 1 ? '' : 's'}` : 'Unchanged'}</span>
            <span className="owner" style={{ whiteSpace: 'normal' }}>{group.summary}</span>
          </TaskButton>)}
        </div>
        <div className="rail-footer">
          {review.activation && <SmallButton type="button" onClick={() => showFrozen ? setShowFrozen(false) : void openFrozen()}><FileText size={14} /> {showFrozen ? 'Back to the live review' : 'View the signed statement'}</SmallButton>}
          <p>{showFrozen && frozen ? `Statement signed ${formatDateTime(frozen.record.approved_at)} by ${frozen.record.approved_by} · hash ${frozen.reviewHash.slice(0, 12)}` : review.fingerprint ? `Configuration fingerprint ${review.fingerprint.slice(0, 12)}` : ''}</p>
        </div>
      </TaskRail>

      <Work aria-label="Selected consequence group">
        <div className="work-heading">
          <div><h2>{shownCurrent?.label}</h2><p>{showFrozen ? 'The statement the checker signed, as it was at approval.' : 'Before and after, with the working for every figure.'}</p></div>
        </div>
        <Readable>{shownCurrent && <GroupPane group={shownCurrent} termControls={showFrozen ? undefined : termControls} />}</Readable>

        {!showFrozen && (review.gates || []).length > 0 && <Panel>
          <h3>Activation gates</h3>
          <Gates>{review.gates!.map(gate => <li key={gate.key} data-passed={gate.passed}>
            {gate.passed ? <Check size={14} color="#7cddbe" /> : <CircleDot size={14} color={gate.key === 'futureDated' ? '#a8b6ca' : '#edcc82'} />}
            <span>{gate.passed ? `${gate.key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()} — passes` : gate.key === 'futureDated' ? 'Effective date is ahead of the business date — approval schedules the activation' : gate.message}</span>
          </li>)}</Gates>
        </Panel>}

        {review.activation && <Panel>
          <h3>Approval record</h3>
          <Table>
            <tbody>
              <tr><td className="muted">Status</td><td>{review.activation.status.toLowerCase()}{review.activation.scheduled ? ' (scheduled)' : ''}</td></tr>
              <tr><td className="muted">Approved</td><td>{review.activation.approved_by} · {formatDateTime(review.activation.approved_at)}</td></tr>
              {review.activation.activated_at && <tr><td className="muted">Activated</td><td>{review.activation.activated_by} · executed by {review.activation.executed_by} · {formatDateTime(review.activation.activated_at)}</td></tr>}
              <tr><td className="muted">Book of record</td><td>{review.activation.book_of_record_status || '—'}{review.activation.marker_trade_id ? ` · marker trade ${review.activation.marker_trade_id}` : ''}{review.activation.true_up_event_id ? ` · true-up event ${review.activation.true_up_event_id}` : ''}</td></tr>
              {review.activation.failure_reason && <tr><td className="muted">Last attempt</td><td style={{ color: '#f8a5a5' }}>{review.activation.failure_reason} ({review.activation.attempts} attempt{review.activation.attempts === 1 ? '' : 's'})</td></tr>}
            </tbody>
          </Table>
        </Panel>}
      </Work>
    </Layout>}

    {required.length > 0 && ['APPROVE_AND_ACTIVATE', 'APPROVE_AND_SCHEDULE'].includes(decision.action) && <Panel>
      <h3>Before you approve</h3>
      {required.map(item => <Ack key={item.key}><input type="checkbox" checked={acks.has(item.key)} onChange={event => setAcks(previous => { const next = new Set(previous); if (event.target.checked) next.add(item.key); else next.delete(item.key); return next; })} /><span>{item.text}</span></Ack>)}
    </Panel>}
    {['APPROVE_AND_ACTIVATE', 'APPROVE_AND_SCHEDULE'].includes(decision.action) && <Panel>
      <h3>Notes for the record (optional)</h3>
      <textarea value={notes} onChange={event => setNotes(event.target.value)} placeholder="What you checked and why you approve" />
    </Panel>}

    {withdrawOpen && <Panel role="dialog" aria-label="Withdraw this amendment">
      <h3>Withdraw version {review.versionNo}</h3>
      <p>The staged amendment closes with your reason; the active contract is unchanged. A withdrawn version stays in the history.</p>
      <textarea value={withdrawReason} onChange={event => setWithdrawReason(event.target.value)} placeholder="Why this amendment is withdrawn" />
      <div className="row">
        <Button $variant="danger" type="button" disabled={busy !== null || !withdrawReason.trim()}
          onClick={() => void act('withdraw', () => withdrawAmendment(versionId, withdrawReason.trim()), `Version ${review.versionNo} withdrawn.`).then(() => setWithdrawOpen(false))}>
          <Undo2 size={14} /> {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw'}
        </Button>
        <Button $variant="quiet" type="button" onClick={() => setWithdrawOpen(false)}>Keep it</Button>
      </div>
    </Panel>}

    <DecisionBar aria-label="Amendment decision">
      <div><strong>{decision.label}</strong><span>{decision.reason}{required.length > 0 && !allAcked && ['APPROVE_AND_ACTIVATE', 'APPROVE_AND_SCHEDULE'].includes(decision.action) ? ` · ${required.length - Array.from(acks).filter(key => required.some(item => item.key === key)).length} acknowledgement(s) still to give` : ''}</span></div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {review.withdrawable && !withdrawOpen && <Button $variant="quiet" type="button" onClick={() => setWithdrawOpen(true)}>Withdraw</Button>}
        {primaryButton()}
      </div>
    </DecisionBar>
    <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
  </Surface>;
};

export default AmendmentReviewPage;
